import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { Iconify, useIsTruncated } from '@mfw/components';
import Box from '@mui/material/Box';

import { NavigationItem } from '../../../../components';
import { useSidebarSectionCollapse } from '../useSidebarSectionCollapse';
import { usePathname, useQueryParams } from '../../../../router/hooks';

export interface SidebarSectionItem {
  label: string;
  icon: string;
  to?: string;
  testId: string;
  soon?: boolean;
  hideable?: boolean;
  /** A short pill after the label, e.g. a price. */
  badge?: string;
  /**
   * Sub-pages of this row, shown indented beneath it while the reader is on
   * the row's own page. Each child points at the same path with a different
   * `?tab=`, so a child is selected by its tab, not by its path.
   */
  children?: SidebarSectionItem[];
}

const tabOf = (to?: string) =>
  new URLSearchParams(to?.split('?')[1] ?? '').get('tab');

const pathOf = (to?: string) => to?.split('?')[0];

/**
 * A row's sub-pages, indented beneath it — only while the reader is on the
 * row's own page. Its own component so that only a row WITH sub-pages reads
 * the location.
 */
const SidebarItemChildren = ({
  item,
  onNavigate,
}: {
  item: SidebarSectionItem;
  onNavigate?: () => void;
}) => {
  const pathname = usePathname();
  const { get } = useQueryParams<{ tab: string }>();
  if (pathname !== pathOf(item.to)) {
    return null;
  }
  const currentTab = get('tab');

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={0.25}
      pl={3}
      data-testid={`${item.testId}-children`}
    >
      {item.children?.map((child, index) => (
        <NavigationItem
          key={child.testId}
          label={child.label}
          icon={child.icon}
          to={child.to}
          onClick={onNavigate}
          data-testid={child.testId}
          selectedByPropOnly
          selected={
            currentTab === tabOf(child.to) ||
            (currentTab === null && index === 0)
          }
          sx={{ minHeight: 32, py: 0.25, fontSize: 13 }}
        />
      ))}
    </Box>
  );
};

export interface SidebarSectionProps {
  id: string;
  label?: string;
  items: SidebarSectionItem[];
  persistKey: string;
  isMiniBarAvailable?: boolean;
  onNavigate?: () => void;
  soonLabel: string;
}

/**
 * One nav grouping: an optional clickable, collapsible caption header
 * followed by its items. With no `label` (the account-level top block) the
 * header is skipped entirely and the section is always open — matching the
 * design's "unlabeled section has no header" spec.
 */
const SidebarSection = ({
  id,
  isMiniBarAvailable,
  items,
  label,
  onNavigate,
  persistKey,
  soonLabel,
}: SidebarSectionProps) => {
  const { isOpen, toggle } = useSidebarSectionCollapse(persistKey);
  // Same "tooltip only when clipped" as the rows beneath (F2, bugz-6). The
  // header block is dropped in the mini bar while this component survives,
  // which is exactly the remount the hook's callback ref exists for.
  const [captionRef, isCaptionTruncated] = useIsTruncated(label);

  // No room for section chrome in the icon-only mini bar — degrade to a
  // flat, always-open icon list, same as the mini bar already does for the
  // legacy `ListSubheader` captions.
  const open = isMiniBarAvailable || isOpen;

  if (items.length === 0) return null;

  return (
    <Box display="flex" flexDirection="column" gap={0.25} pb={0.75}>
      {label && !isMiniBarAvailable && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          px={1.25}
          pt={1}
          pb={0.75}
          onClick={toggle}
          data-testid={`sidebar-section-${id}-toggle`}
          sx={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {/* `noWrap` + `minWidth: 0`: the caption carries the organization
              name (up to 255 chars); as a `flex: 1` item its minimum size
              is its content, so without `minWidth: 0` it never shrinks to
              the column and the ellipsis never fires (G4, bugz-3). */}
          <Typography
            noWrap
            ref={captionRef}
            title={isCaptionTruncated ? label : undefined}
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 11,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontWeight: 700,
              lineHeight: 1,
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
          <Iconify
            icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'}
            size="small"
            sx={{ color: 'action.active' }}
          />
        </Box>
      )}
      <Collapse in={open}>
        <Box display="flex" flexDirection="column" gap={0.25}>
          {items.map((item) => {
            return (
              <Box key={item.testId} display="flex" flexDirection="column">
                <NavigationItem
                  label={item.label}
                  icon={item.icon}
                  to={item.to}
                  disabled={item.soon}
                  onClick={onNavigate}
                  mini={isMiniBarAvailable}
                  data-testid={item.testId}
                  endAdornment={
                    (item.hideable || item.soon || item.badge) &&
                    !isMiniBarAvailable ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {item.badge && (
                          <Box
                            component="span"
                            data-testid={`${item.testId}-badge`}
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                              color: 'warning.contrastText',
                              bgcolor: 'warning.main',
                              borderRadius: '4px',
                              px: 0.6,
                              py: 0.15,
                            }}
                          >
                            {item.badge}
                          </Box>
                        )}
                        {item.hideable && (
                          <Iconify
                            icon="mdi:eye-off-outline"
                            size="tiny"
                            sx={{ color: 'action.active' }}
                          />
                        )}
                        {item.soon && (
                          <Box
                            component="span"
                            sx={{
                              fontSize: 9.5,
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                              color: 'text.disabled',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: '4px',
                              px: 0.6,
                              py: 0.15,
                            }}
                          >
                            {soonLabel}
                          </Box>
                        )}
                      </Box>
                    ) : undefined
                  }
                />
                {!!item.children?.length && !isMiniBarAvailable && (
                  <SidebarItemChildren item={item} onNavigate={onNavigate} />
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default SidebarSection;
